import {
    keepPreviousData,
    QueryKey,
    useQuery,
    UseQueryOptions,
} from "@tanstack/react-query";
import { WithRequiredProperties } from "@common/types/base/generics";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { Entity } from "@common/types/entity";
import { RequiredAction } from "@common/requiredActions";
import { getTransactions, getSuggestedMatches } from "@api/transaction.api";
import {
    GetTransactionsResponse,
    TransactionListItem,
    TransactionSort,
} from "@common/schemas/transaction.schema";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { withCanonicalDateFilters } from "@common/helpers/date";
import { queryClient } from "../../queryClient";
import { TransactionsFilters } from "./filters/lib";
import { TRANSACTIONS_STATISTICS_QUERY_KEY } from "./useTransactionsStatisticsQuery";

export const TRANSACTIONS_PAGE_SIZE = 100;
export const TRANSACTIONS_QUERY_KEY = "transactions";
export const ACCRUAL_REVIEW_QUERY_KEY = "accrual-review-transactions";
export const SCHEDULE_REVIEW_QUERY_KEY = "schedule-review-transactions";
export const RECONCILIATION_CLEAR_QUERY_KEY =
    "reconciliation-clear-transactions";
export const TRANSACTIONS_CACHE_DURATION = 1000 * 15; // 15 seconds

export interface UseTransactionsQueryParam {
    filters: WithRequiredProperties<TransactionsFilters, "entityIds">;
    sort?: TransactionSort;
    page: number;
    limit?: number;
    matchingTransactionId?: number;
    workspaceId: string;
    skipCount?: boolean;
    includeMatchDetails?: boolean;
    includeAccrualMatchDetails?: boolean;
    includeAccrualMatchSuggestions?: boolean;
}

type UseTransactionsQueryOptions = Omit<
    UseQueryOptions<GetTransactionsResponse>,
    "queryKey" | "queryFn"
>;

export const loadTransactions = ({
    filters,
    sort,
    page,
    limit,
    workspaceId,
    skipCount,
    includeMatchDetails,
    includeAccrualMatchDetails,
    includeAccrualMatchSuggestions,
}: UseTransactionsQueryParam): Promise<GetTransactionsResponse> =>
    (filters.entityIds || []).length > 0
        ? getTransactions({
              page: page - 1,
              limit: limit ?? TRANSACTIONS_PAGE_SIZE,
              filters: convertFiltersToDto(filters),
              sort,
              workspaceId,
              skipCount,
              includeMatchDetails,
              includeAccrualMatchDetails,
              includeAccrualMatchSuggestions,
          })
        : Promise.resolve({ data: [] });

const loadMatchingTransactions = ({
    filters,
    sort,
    matchingTransactionId,
}: Omit<
    UseTransactionsQueryParam,
    "matchingTransactionId" | "page" | "limit"
> & {
    matchingTransactionId: number;
}): Promise<GetTransactionsResponse> =>
    (filters.entityIds || []).length > 0
        ? getSuggestedMatches({
              transactionId: matchingTransactionId,
              filters: convertFiltersToDto(filters),
              sort,
          })
        : Promise.resolve({ data: [] });

export const buildTransactionsQueryKey = ({
    filters,
    sort,
    page,
    limit,
    matchingTransactionId,
    workspaceId,
    skipCount,
    includeMatchDetails,
    includeAccrualMatchDetails,
    includeAccrualMatchSuggestions,
    queryKeyPrefix,
}: UseTransactionsQueryParam & { queryKeyPrefix?: string }): QueryKey => [
    queryKeyPrefix ?? TRANSACTIONS_QUERY_KEY,
    workspaceId,
    filters,
    sort,
    page,
    limit,
    matchingTransactionId,
    skipCount,
    includeMatchDetails,
    includeAccrualMatchDetails,
    includeAccrualMatchSuggestions,
];

export function useTransactionsQuery(
    {
        filters,
        sort,
        page,
        limit,
        matchingTransactionId,
        skipCount,
        includeMatchDetails,
        includeAccrualMatchDetails,
        includeAccrualMatchSuggestions,
        queryKeyPrefix,
    }: Omit<UseTransactionsQueryParam, "workspaceId"> & {
        queryKeyPrefix?: string;
    },
    options: UseTransactionsQueryOptions = {},
) {
    const workspace = useActiveWorkspace();
    const workspaceId = workspace?.id ?? "";
    const qk = buildTransactionsQueryKey({
        filters,
        sort,
        page,
        limit,
        matchingTransactionId,
        workspaceId,
        skipCount,
        includeMatchDetails,
        includeAccrualMatchDetails,
        includeAccrualMatchSuggestions,
        queryKeyPrefix,
    });

    return {
        query: useQuery({
            queryKey: qk,
            queryFn: () =>
                matchingTransactionId
                    ? loadMatchingTransactions({
                          filters,
                          sort,
                          matchingTransactionId,
                          workspaceId,
                      })
                    : loadTransactions({
                          filters,
                          sort,
                          page,
                          limit,
                          workspaceId,
                          skipCount,
                          includeMatchDetails,
                          includeAccrualMatchDetails,
                          includeAccrualMatchSuggestions,
                      }),
            staleTime: TRANSACTIONS_CACHE_DURATION,
            placeholderData: keepPreviousData,
            ...options,
        }),
        queryKey: qk,
    };
}

export const preloadTransactionsQuery = ({
    filters,
    sort,
    page,
    limit,
    workspaceId,
    skipCount,
}: UseTransactionsQueryParam): void => {
    queryClient.prefetchQuery({
        queryKey: buildTransactionsQueryKey({
            filters,
            sort,
            page,
            limit,
            workspaceId,
            skipCount,
        }),
        queryFn: () =>
            loadTransactions({
                filters,
                sort,
                page,
                limit,
                workspaceId,
                skipCount,
            }),
        staleTime: TRANSACTIONS_CACHE_DURATION,
    });
};

export function convertFiltersToDto(
    filters: WithRequiredProperties<TransactionsFilters, "entityIds">,
): TransactionFiltersDto;
export function convertFiltersToDto<T extends TransactionsFilters>(
    filters: T,
    entities: Entity[],
): TransactionFiltersDto;
export function convertFiltersToDto<T extends TransactionsFilters>(
    filters: T,
    entities?: Entity[],
): TransactionFiltersDto {
    return {
        ...withCanonicalDateFilters(filters),
        requiredAction: filters.requiredAction ? true : undefined,
        requiredActions: filters.requiredActions
            ? (filters.requiredActions as RequiredAction[])
            : undefined,
        entityIds:
            filters.entityIds ?? entities?.map((entity) => entity.id) ?? [],
    };
}

export function updateTransactionInCache(
    transactionId: number,
    updater: (transaction: TransactionListItem) => TransactionListItem,
): void {
    queryClient.setQueriesData<GetTransactionsResponse>(
        { queryKey: [TRANSACTIONS_QUERY_KEY] },
        (prev) => {
            if (!prev) {
                return prev;
            }
            return {
                ...prev,
                data: prev.data.map((t) =>
                    t.id === transactionId ? updater(t) : t,
                ),
            };
        },
    );
}

export async function invalidateTransactionsQueries(): Promise<void> {
    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: [TRANSACTIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
            queryKey: [TRANSACTIONS_STATISTICS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
            queryKey: [ACCRUAL_REVIEW_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
            queryKey: [SCHEDULE_REVIEW_QUERY_KEY],
        }),
    ]);
}

// Marks transactions list queries stale without triggering an immediate
// refetch of the currently-active query. The newly-activated query refetches
// fresh data on mount, while the tab being left avoids a wasted request.
// Statistics/accrual queries are intentionally left untouched.
export async function invalidateTransactionsListCache(): Promise<void> {
    await queryClient.invalidateQueries({
        queryKey: [TRANSACTIONS_QUERY_KEY],
        refetchType: "none",
    });
}
